import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { uploadPhoto } from "./uploadPhoto";
import NavBar from "./NavBar";

const API_BASE_URL = "https://api.itrustsecure.com";
function getToken() { return localStorage.getItem("itrust_token"); }
function authHeaders() { return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }; }

const DISPUTE_TYPES = [
  { value: "NOT_AS_DESCRIBED", label: "Not as described", icon: "❌" },
  { value: "DAMAGED", label: "Item arrived damaged", icon: "💔" },
  { value: "NOT_RECEIVED", label: "Item not received", icon: "📭" },
  { value: "OTHER", label: "Other issue", icon: "⚠️" },
];

export default function DisputePage({ user, onLogout }) {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [disputeType, setDisputeType] = useState("NOT_AS_DESCRIBED");
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/orders/${orderId}`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setOrder(d)).finally(() => setLoading(false));
  }, [orderId]);

  async function handlePhotoUpload(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = await Promise.all(files.map(f => uploadPhoto(f)));
      setPhotos(prev => [...prev, ...urls]);
    } catch { window.alert("Photo upload failed. Please try again."); }
    finally { setUploading(false); e.target.value = ""; }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!reason.trim()) { window.alert("Please provide a brief summary"); return; }
    if (!description.trim()) { window.alert("Please provide a detailed description"); return; }
    if (photos.length === 0) { window.alert("At least one photo is required as evidence"); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/disputes`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ orderId, openedById: user.id, reason, disputeType, description, photoUrls: photos })
      });
      if (res.ok) { window.alert("Dispute filed successfully. The seller has been notified."); navigate("/orders"); }
      else { const d = await res.json(); window.alert(d.error || "Could not file dispute"); }
    } catch { window.alert("Something went wrong"); }
    finally { setSubmitting(false); }
  }

  const styles = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "transparent", color: "#0f172a", minHeight: "100vh" },
    main: { maxWidth: 680, margin: "0 auto", padding: "32px 20px" },
    title: { fontSize: 28, fontWeight: 800, marginBottom: 8, color: "#0f172a" },
    subtitle: { fontSize: 14, color: "#64748b", marginBottom: 28 },
    warning: { display: "flex", gap: 12, background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.25)", borderRadius: 12, padding: 16, marginBottom: 24 },
    label: { fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 6, display: "block" },
    input: { width: "100%", padding: "12px 14px", background: "rgba(255,255,255,0.8)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, color: "#0f172a", fontSize: 14, marginBottom: 16, boxSizing: "border-box", outline: "none" },
    textarea: { width: "100%", padding: "12px 14px", background: "rgba(255,255,255,0.8)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, color: "#0f172a", fontSize: 14, marginBottom: 16, boxSizing: "border-box", minHeight: 120, resize: "vertical", outline: "none" },
    typeGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 },
    typeCard: (active) => ({ padding: "14px 16px", borderRadius: 12, border: "1px solid", borderColor: active ? "#3b82f6" : "rgba(59,130,246,0.15)", background: active ? "rgba(59,130,246,0.08)" : "rgba(255,255,255,0.7)", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, backdropFilter: "blur(6px)" }),
    photoGrid: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 },
    photoThumb: { width: 80, height: 80, objectFit: "cover", borderRadius: 10, border: "1px solid rgba(59,130,246,0.15)" },
    photoRemove: { position: "absolute", top: -6, right: -6, background: "#ef4444", border: "none", borderRadius: "50%", width: 20, height: 20, color: "#fff", cursor: "pointer", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" },
    uploadBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px 20px", background: "rgba(59,130,246,0.06)", border: "1px dashed rgba(59,130,246,0.3)", borderRadius: 12, color: "#3b82f6", cursor: "pointer", fontWeight: 700, fontSize: 14, marginBottom: 20, width: "100%", boxSizing: "border-box" },
    submitBtn: { background: "#dc2626", color: "#fff", border: "none", padding: "14px 20px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 15, width: "100%", opacity: submitting ? 0.5 : 1, boxShadow: "0 4px 12px rgba(220,38,38,0.3)" },
    backBtn: { background: "transparent", border: "none", color: "#64748b", cursor: "pointer", fontSize: 14, marginBottom: 20, padding: 0 },
    sectionTitle: { fontSize: 16, fontWeight: 700, marginBottom: 12, color: "#1e293b" },
  };

  if (loading) return (
    <div style={styles.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh", color: "#94a3b8" }}>Loading...</div>
    </div>
  );

  return (
    <div style={styles.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={styles.main}>
        <button style={styles.backBtn} onClick={() => navigate("/orders")}>← Back to Orders</button>
        <div style={styles.title}>File a Dispute</div>
        <div style={styles.subtitle}>Order #{orderId?.slice(0, 8)}</div>

        <div style={styles.warning}>
          <span style={{ fontSize: 24 }}>⚠️</span>
          <div>
            <div style={{ fontWeight: 700, color: "#b45309", marginBottom: 4 }}>Before you file a dispute</div>
            <div style={{ fontSize: 13, color: "#b45309" }}>Photo evidence is required. False disputes may result in account suspension. The seller has 48 hours to respond.</div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.sectionTitle}>What is the issue?</div>
          <div style={styles.typeGrid}>
            {DISPUTE_TYPES.map(type => (
              <div key={type.value} style={styles.typeCard(disputeType === type.value)} onClick={() => setDisputeType(type.value)}>
                <span style={{ fontSize: 20 }}>{type.icon}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: disputeType === type.value ? "#1e40af" : "#475569" }}>{type.label}</span>
              </div>
            ))}
          </div>

          <label style={styles.label}>Brief Summary *</label>
          <input style={styles.input} placeholder="e.g. Item received was completely different from listing" value={reason} onChange={e => setReason(e.target.value)} required />

          <label style={styles.label}>Detailed Description *</label>
          <textarea style={styles.textarea} placeholder="Describe exactly what you received vs what was listed..." value={description} onChange={e => setDescription(e.target.value)} required />

          <div style={styles.sectionTitle}>Photo Evidence *</div>
          <p style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>Upload photos directly from your device. At least one photo is required.</p>

          {photos.length > 0 && (
            <div style={styles.photoGrid}>
              {photos.map((url, i) => (
                <div key={i} style={{ position: "relative" }}>
                  <img src={url} alt={`Evidence ${i + 1}`} style={styles.photoThumb} />
                  <button type="button" style={styles.photoRemove} onClick={() => setPhotos(photos.filter((_, pi) => pi !== i))}>✕</button>
                </div>
              ))}
            </div>
          )}

          {uploading ? (
            <div style={{ ...styles.uploadBtn, opacity: 0.6 }}>⏳ Uploading photos...</div>
          ) : (
            <>
              <div style={styles.uploadBtn} onClick={() => fileRef.current?.click()}>📷 {photos.length > 0 ? "Add More Photos" : "Upload Photo Evidence"}</div>
              <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handlePhotoUpload} />
            </>
          )}

          <button type="submit" style={styles.submitBtn} disabled={submitting || uploading}>
            {submitting ? "Filing Dispute..." : "File Dispute"}
          </button>
        </form>
      </div>
    </div>
  );
}